import { useCallback, useEffect, useState } from "react";
import {
  AlertIcon,
  CheckboxRow,
  DashButton,
  DataSurface,
  Note,
  PrivacyIcon,
  type SurfaceState,
} from "../../shared/components";
import { acceptTerms, getTerms, type TermsStatus } from "../../shared/api/auth";

/**
 * Change 19 §6 — *"on the first School Admin login, Terms & Conditions must be
 * accepted before accessing the dashboard."*
 *
 * 🔴 THIS SCREEN IS NOT THE ENFORCEMENT AND MUST NOT BE MISTAKEN FOR IT.
 * `TermsGuard` on the server refuses every route but these two until the
 * acceptance is written; an overlay in a browser is removed by a `display: none`
 * in the dev tools, and an acceptance you cannot prove is one you did not
 * obtain. What this is for is the other half: a 403 with no explanation is not a
 * way to ask somebody to agree to something.
 *
 * 🔴 IT REPLACES THE DASHBOARD RATHER THAN COVERING IT. A modal over a working
 * screen invites the reading that the screen underneath is usable — it is not,
 * every request behind it is refused — and it puts a dismiss affordance on the
 * one thing that must not be dismissible. There is no close button here on
 * purpose; the only ways out are agreeing and signing out.
 *
 * ⚠️ THE CHECKBOX IS NOT DECORATION EITHER. A button on its own records a click
 * on a page; a deliberate "I have read this" is the smallest thing that makes
 * the record mean what it says, and it is the difference between an agreement
 * and a speed bump.
 */
export interface TermsGateProps {
  token: string;
  /** Called once the acceptance is written, so the router can let them past. */
  onAccepted: () => void;
  /**
   * The only other way out. A person who does not agree must be able to leave
   * rather than be trapped on a screen with one button.
   */
  onSignOut: () => void;
}

export function TermsGate({ token, onAccepted, onSignOut }: TermsGateProps) {
  const [status, setStatus] = useState<TermsStatus | null>(null);
  const [state, setState] = useState<SurfaceState>("loading");
  const [read, setRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    getTerms({ token, signal: controller.signal })
      .then((row) => {
        setStatus(row);
        setState("ready");
      })
      .catch(() => {
        /*
         * ⚠️ AN ERROR HERE IS AN ERROR STATE, NOT SILENCE — the opposite of the
         * broadcast strip's rule, and for the opposite reason. That strip is
         * decoration above a working screen; this IS the screen, and a blank
         * page with no retry is somebody locked out with nothing to press.
         */
        setState("error");
      });
    return () => {
      controller.abort();
    };
  }, [token]);

  const agree = useCallback(() => {
    setBusy(true);
    setFailed(false);
    acceptTerms({ token })
      .then(() => {
        onAccepted();
      })
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setBusy(false);
      });
  }, [token, onAccepted]);

  return (
    <main className="console min-h-screen bg-canvas px-4 py-10">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 rounded-lg border border-line bg-surface p-8 shadow-card">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-surface-muted text-ink-soft">
            <PrivacyIcon size={20} />
          </div>
          <div>
            <h1 className="font-dash text-dash-xl font-extrabold text-ink">
              Before you open the dashboard
            </h1>
            <p className="mt-1 max-w-[60ch] font-dash text-dash-sm text-muted">
              Kalqy holds records about children at your school. Please read what that
              means, and agree to it, before going any further.
            </p>
          </div>
        </div>

        <DataSurface
          state={state}
          skeletonRows={4}
          errorMessage="Couldn’t load the terms."
          onRetry={() => {
            setState("loading");
            getTerms({ token })
              .then((row) => {
                setStatus(row);
                setState("ready");
              })
              .catch(() => {
                setState("error");
              });
          }}
        >
          {status === null ? null : (
            <div className="flex flex-col gap-5">
              <div className="max-h-[22rem] overflow-y-auto rounded-md border border-line bg-surface-subtle p-5">
                {status.sections.map((section) => (
                  <section key={section.heading} className="mb-5 last:mb-0">
                    <h2 className="font-dash text-dash-sm font-extrabold text-ink">
                      {section.heading}
                    </h2>
                    <p className="mt-1 max-w-[70ch] font-dash text-dash-sm text-ink-soft">
                      {section.body}
                    </p>
                  </section>
                ))}
              </div>

              {/* 🔴 SAID OUT LOUD, BECAUSE THE ALTERNATIVE IS SOMEBODY AGREEING
                  TWICE AND NOT KNOWING WHY. When the text changes, an admin who
                  accepted last year's is gated again — this is the sentence that
                  explains a screen they have seen before. */}
              {status.acceptedVersion !== null &&
              status.acceptedVersion !== status.version ? (
                <Note tone="info" icon={<AlertIcon size={16} />}>
                  These terms have changed since you last agreed to them.
                </Note>
              ) : null}

              <CheckboxRow
                checked={read}
                onChange={setRead}
                hint="You are agreeing on behalf of your school."
              >
                I have read this and I agree to it
              </CheckboxRow>

              <div className="flex flex-wrap items-center gap-3">
                <DashButton onClick={agree} disabled={!read || busy}>
                  {busy ? "Saving…" : "Agree and continue"}
                </DashButton>
                {/* The other way out. Nobody is trapped on a screen with one button. */}
                <button
                  type="button"
                  onClick={onSignOut}
                  className="font-dash text-dash-xs font-bold text-muted underline hover:text-ink cursor-pointer"
                >
                  Sign out instead
                </button>
              </div>

              {failed ? (
                <Note tone="danger" icon={<AlertIcon size={16} />}>
                  That was not saved. Nothing has changed — try again.
                </Note>
              ) : null}

              <p className="font-dash text-dash-xs text-muted">
                Version {status.version}.
              </p>
            </div>
          )}
        </DataSurface>
      </div>
    </main>
  );
}
